import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Heart, Minus, Plus, Share2, CheckCircle, Truck, RefreshCw, Shield, Star } from 'lucide-react';
import { addToCart, addToWishlist, removeFromWishlist, getToken, removeToken, removeUser } from '../api/auth';
import { useCurrency } from '../context/CurrencyContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/* ── Badge colour map ─────────────────────────────────────── */
const BADGE_STYLES = {
  'new':        { bg: '#1C1A18', color: '#fff' },
  'bestseller': { bg: '#B08D57', color: '#fff' },
  'trending':   { bg: '#7B4F2E', color: '#fff' },
  'limited':    { bg: '#C0392B', color: '#fff' },
  'sale':       { bg: '#C0392B', color: '#fff' },
  'featured':   { bg: '#4A6741', color: '#fff' },
  default:      { bg: '#1C1A18', color: '#fff' },
};

const getBadgeStyle = (badge) => {
  if (!badge) return null;
  const key = badge.toLowerCase().replace(/\s+/g, '');
  return BADGE_STYLES[key] || BADGE_STYLES.default;
};

const TRUST_BADGES = [
  { icon: CheckCircle, title: 'COD Available',  sub: 'Easy Returns & Quick Exchanges' },
  { icon: Truck,       title: 'Fast Delivery',   sub: '2–7 Business Days Across India' },
  { icon: RefreshCw,   title: 'Free Shipping',   sub: 'On Prepaid Orders Above ₹999'  },
  { icon: Shield,      title: 'Premium Quality', sub: 'Stones, Plating & Craftsmanship'},
];

/* ── Toast ─────────────────────────────────────────────────── */
const Toast = ({ msg, show }) => (
  <AnimatePresence>
    {show && (
      <motion.div className="rpd-toast"
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }} transition={{ duration: 0.28 }}>
        <CheckCircle size={13} /> {msg}
      </motion.div>
    )}
  </AnimatePresence>
);

/* ── Mini card for "More from reel" ────────────────────────── */
const MiniCard = ({ product, reelId, navigate }) => {
  const { formatPrice } = useCurrency();
  const hasDiscount  = product.sale_price && product.sale_price < product.original_price;
  const displayPrice = hasDiscount ? product.sale_price : product.original_price;
  const mrpPrice     = hasDiscount ? product.original_price : null;

  return (
    <div className="rpd-mc" onClick={() => navigate(`/reels/${reelId}/${product.id}`)}>
      <div className="rpd-mc-img">
        <img src={product.images?.[0] || product.image_1} alt={product.name} loading="lazy" />
      </div>
      <div className="rpd-mc-body">
        <p className="rpd-mc-name">{product.name}</p>
        <div className="rpd-mc-price-row">
          <span className="rpd-mc-price">{formatPrice(displayPrice)}</span>
          {mrpPrice && <span className="rpd-mc-mrp">{formatPrice(mrpPrice)}</span>}
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
const ReelProductDetail = () => {
  const { reelId, productId } = useParams();
  const navigate = useNavigate();

  const { formatPrice } = useCurrency();

  const [visible,       setVisible]       = useState(false);
  const [product,       setProduct]       = useState(null);
  const [reel,          setReel]          = useState(null);
  const [otherProducts, setOtherProducts] = useState([]);
  const [activeImg,     setActiveImg]     = useState(0);
  const [qty,           setQty]           = useState(1);
  const [wished,       setWished]       = useState(false);
  const [wishLoading,  setWishLoading]  = useState(false);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [toast,         setToast]         = useState({ show: false, msg: '' });
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize,  setSelectedSize]  = useState('');

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!reelId || !productId) return;
    setLoading(true);
    setError('');
    setActiveImg(0);
    setQty(1);
    setSelectedColor('');
  setSelectedSize('');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const fetchData = async () => {
      try {
        const [productRes, reelRes, allProdsRes] = await Promise.all([
          fetch(`${API}/products/${productId}`),
          fetch(`${API}/reels/${reelId}`),
          fetch(`${API}/reels/${reelId}/products`),
        ]);

        if (!productRes.ok) throw new Error(`Product fetch failed: ${productRes.status}`);
        if (!reelRes.ok)    throw new Error(`Reel fetch failed: ${reelRes.status}`);

        const productData  = await productRes.json();
        const reelData     = await reelRes.json();
        const allProdsData = await allProdsRes.json();

        if (productData.success)  setProduct(productData.product);
        else                      setError(productData.error || 'Product not found');

        if (reelData.success)     setReel(reelData.reel);

        if (allProdsData.success) {
          setOtherProducts(
            (allProdsData.products || [])
              .filter(p => String(p.id) !== String(productId))
              .slice(0, 4)
          );
        }
      } catch (err) {
        console.error('ReelProductDetail fetch error:', err);
        setError(err.message || 'Failed to connect to server');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reelId, productId]);

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2200);
  };

  const handleAddToCart = async () => {
  if (product.stock_qty === 0) { showToast('This product is sold out'); return; }
  if (!getToken()) { navigate('/account'); return; }
  const colorCount = [...new Set(product.colors?.filter(c => c && c.trim()) ?? [])].length;
  const sizeCount  = [...new Set(product.sizes?.filter(s => s && s.trim()) ?? [])].length;

  if (colorCount > 1 && !selectedColor) {
    showToast('Please select a color'); return;
  }
  if (sizeCount > 1 && !selectedSize) {
    showToast('Please select a size'); return;
  }
    try {
      await addToCart(product.id, qty, selectedColor || null, selectedSize || null);
      showToast(`${product.name} added to cart!`);
      window.dispatchEvent(new Event('cart-updated'));
    } catch (err) {
      const msg = err.message?.toLowerCase() || '';
      if (msg.includes('invalid') || msg.includes('expired') || msg.includes('token') || msg.includes('unauthorized')) {
        removeToken(); removeUser(); navigate('/account'); return;
      }
      showToast(err.message || 'Failed to add to cart');
    }
  };

  const handleBuyNow = () => {
  if (product.stock_qty === 0) { showToast('This product is sold out'); return; }
  if (!getToken()) { navigate('/account'); return; }
  const colorCount = [...new Set(product.colors?.filter(c => c && c.trim()) ?? [])].length;
  const sizeCount  = [...new Set(product.sizes?.filter(s => s && s.trim()) ?? [])].length;

  if (colorCount > 1 && !selectedColor) {
    showToast('Please select a color'); return;
  }
  if (sizeCount > 1 && !selectedSize) {
    showToast('Please select a size'); return;
  }

  // ✅ Add this stock quantity check
  if (qty > product.stock_qty) {
    showToast(`Only ${product.stock_qty} units available`);
    return;
  }

  navigate('/checkout', {
    state: {
      items: [{
        id:    product.id,
        name:  product.name,
        price: product.sale_price ?? product.original_price,
        image: product.images?.[0] || product.image_1,
        qty,
        color: selectedColor || null,
        size:  selectedSize  || null,
      }]
    }
  });
};


  // ADD after handleBuyNow function:
  const toggleWishlist = async () => {
    if (!getToken()) { navigate('/account'); return; }
    if (wishLoading) return;
    setWishLoading(true);
    try {
      if (wished) {
        await removeFromWishlist(product.id);
        setWished(false);
        showToast('Removed from wishlist');
      } else {
        await addToWishlist(product.id);
        setWished(true);
        showToast('Added to wishlist!');
      }
    } catch (err) {
      showToast(err.message || 'Wishlist error');
    } finally {
      setWishLoading(false);
    }
  };

  /* ── Derived values ── */
  const images = product
    ? (product.images?.length
        ? product.images
        : [product.image_1, product.image_2, product.image_3]
      ).filter(Boolean)
    : [];
  const hasDiscount  = product?.sale_price && product.sale_price < product.original_price;
  const displayPrice = hasDiscount ? product.sale_price : product?.original_price;
  const mrpPrice     = hasDiscount ? product.original_price : null;
  const discountPct  = mrpPrice ? Math.round((1 - displayPrice / mrpPrice) * 100) : 0;
  const savings      = mrpPrice ? Math.round(mrpPrice - displayPrice) : 0;
  const badgeStyle   = product?.badge ? getBadgeStyle(product.badge) : null;
  

  /* ── Loading ── */
  if (loading) return (
    <>
      <style>{pageStyles}</style>
      <div className="rpd-page">
        <div className="rpd-loading">
          <div className="rpd-loading-ring" />
          <p className="rpd-loading-text">Loading product…</p>
        </div>
      </div>
    </>
  );

  /* ── Error ── */
  if (error || !product) return (
    <>
      <style>{pageStyles}</style>
      <div className="rpd-page">
        <div className="rpd-error-state">
          <p className="rpd-error-icon">✦</p>
          <h2 className="rpd-error-title">{error || 'Product not found'}</h2>
          <Link to={`/reels/${reelId}`} className="rpd-error-back">← Back to collection</Link>
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{pageStyles}</style>
      <div className="rpd-page">

        {/* ── Breadcrumb ── */}
        <motion.div
          className="rpd-breadcrumb"
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link to="/">Home</Link>
          <span className="rpd-bc-sep">›</span>
          <Link to="/reels">Reels</Link>
          <span className="rpd-bc-sep">›</span>
          {reel && <Link to={`/reels/${reelId}`}>{reel.title}</Link>}
          {reel && <span className="rpd-bc-sep">›</span>}
          <span className="rpd-bc-cur">{product.name}</span>
        </motion.div>

        {/* ── Main grid ── */}
        <div className="rpd-main">

          {/* Left — gallery */}
          <motion.div
            className="rpd-gallery"
            initial={{ opacity: 0, x: -30 }}
            animate={visible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rpd-main-img-wrap">
              {product.badge && badgeStyle && (
                <span className="rpd-img-badge" style={{ background: badgeStyle.bg, color: badgeStyle.color }}>
                  {product.badge}
                </span>
              )}
              {discountPct > 0 && (
                <span className="rpd-img-off">{discountPct}% Off</span>
              )}
              <div className="rpd-main-img">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={activeImg}
                    src={images.length > 0 ? images[activeImg] : '/fallback.jpg'}
                    alt={product.name}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                  />
                </AnimatePresence>
              </div>
            </div>

            {images.length > 1 && (
              <div className="rpd-thumbs">
                {images.map((img, i) => (
                  <div
                    key={i}
                    className={`rpd-thumb${activeImg === i ? ' active' : ''}`}
                    onClick={() => setActiveImg(i)}
                  >
                    <img src={img} alt={`${product.name} view ${i + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Right — info */}
          <motion.div
            className="rpd-info"
            initial={{ opacity: 0, x: 30 }}
            animate={visible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="rpd-eyebrow">{reel?.title || 'Aamira Jewellery'}</p>
            <h1 className="rpd-name">{product.name}</h1>

            {/* Stars */}
            <div className="rpd-stars">
              <div className="rpd-stars-icons">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} size={13} fill={s <= 4 ? '#B08D57' : 'none'} color="#B08D57" />
                ))}
              </div>
              <span className="rpd-review-count">4.0 (24 reviews)</span>
            </div>

            {/* Price */}
            <div className="rpd-price-block">
              <div className="rpd-price-row">
                <span className="rpd-price">{formatPrice(displayPrice)}</span>
                {mrpPrice && <span className="rpd-mrp">{formatPrice(mrpPrice)}</span>}
                {discountPct > 0 && <span className="rpd-discount-badge">{discountPct}% OFF</span>}
              </div>
              {savings > 0 && (
                <p className="rpd-save">You save {formatPrice(savings)}</p>
              )}
              <p className="rpd-tax-note">Inclusive of all taxes · Free shipping on prepaid</p>
            </div>

            <div className="rpd-divider" />

            {product.description && <p className="rpd-desc">{product.description}</p>}

            {/* Offer text */}
            {product.offer_text && (
              <div className="rpd-offer-box">
                🏷 {product.offer_text}
              </div>
            )}

            {/* Details table */}
            
              <div className="rpd-details">
                {reel && (
                  <div className="rpd-detail-row">
                    <span className="rpd-detail-key">From Reel</span>
                    <span className="rpd-detail-val">{reel.title}</span>
                  </div>
                )}
              </div>

              {/* Color selector — only if product has colors in DB */}
              {product.colors?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <p className="rpd-qty-label">Color</p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {product.colors.map(c => (
                      <button key={c} onClick={() => setSelectedColor(c)} style={{
                        padding: '7px 16px', fontSize: 11, fontFamily: 'Jost,sans-serif',
                        fontWeight: 500, letterSpacing: '0.1em', cursor: 'pointer',
                        border: selectedColor === c ? '1.5px solid #B08D57' : '1px solid #d4c5a9',
                        background: selectedColor === c ? '#1C1A18' : 'transparent',
                        color: selectedColor === c ? '#FAF8F4' : '#3a3530',
                        transition: 'all 0.2s',
                      }}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Size selector — only if product has sizes in DB */}
              {product.sizes?.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <p className="rpd-qty-label">Size</p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {product.sizes.map(s => (
                      <button key={s} onClick={() => setSelectedSize(s)} style={{
                        padding: '7px 16px', fontSize: 11, fontFamily: 'Jost,sans-serif',
                        fontWeight: 500, letterSpacing: '0.1em', cursor: 'pointer',
                        border: selectedSize === s ? '1.5px solid #B08D57' : '1px solid #d4c5a9',
                        background: selectedSize === s ? '#1C1A18' : 'transparent',
                        color: selectedSize === s ? '#FAF8F4' : '#3a3530',
                        transition: 'all 0.2s',
                      }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

            {/* Qty */}
            <p className="rpd-qty-label">Quantity</p>
            <div className="rpd-qty-ctrl">
              <button className="rpd-qty-btn" onClick={() => setQty(q => Math.max(1, q - 1))}>
                <Minus size={13} />
              </button>
              <span className="rpd-qty-val">{qty}</span>
              <button className="rpd-qty-btn" onClick={() => setQty(q => Math.min(product.stock_qty, q + 1))}>
                <Plus size={13} />
              </button>
            </div>

            {/* CTAs — matching ProductDetails exactly */}
            <div className="rpd-actions">
              <button className="rpd-btn-atc" onClick={handleAddToCart} disabled={product.stock_qty === 0}>
              <ShoppingBag size={13} />
              <span>{product.stock_qty === 0 ? 'Sold Out' : 'Add to Cart'}</span>
            </button>
              <button className="rpd-btn-buy" onClick={handleBuyNow} disabled={product.stock_qty === 0}>
              <span>{product.stock_qty === 0 ? 'Sold Out' : 'Buy It Now'}</span>
            </button>
            </div>

            {/* Wishlist + Share row */}
            <div className="rpd-icon-row">
              <button
                className={`rpd-icon-btn${wished ? ' rpd-icon-btn--wished' : ''}`}
                onClick={toggleWishlist}
                disabled={wishLoading}
              >
                <Heart size={13} fill={wished ? '#B08D57' : 'none'} />
                {wishLoading ? 'Please wait…' : wished ? 'Wishlisted' : 'Add to Wishlist'}
              </button>
              <button
                className="rpd-icon-btn"
                onClick={() => { navigator.clipboard?.writeText(window.location.href); showToast('Link copied!'); }}
              >
                <Share2 size={13} /> Share
              </button>
            </div>

            <div className="rpd-divider" />

            {/* Trust badges — matching ProductDetails exactly */}
            <div className="rpd-trust-grid">
              {TRUST_BADGES.map((b, i) => (
                <div key={i} className="rpd-trust-card">
                  <b.icon size={16} className="rpd-trust-icon" />
                  <div>
                    <p className="rpd-trust-title">{b.title}</p>
                    <p className="rpd-trust-sub">{b.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ── More from this reel ── */}
{otherProducts.length > 0 && (
  <div className="rpd-more">
    <div className="rpd-more-head">
      <div>
        <p className="rpd-more-label">From this Reel</p>
        <p className="rpd-more-title">
          More to <em style={{ fontStyle: 'italic', color: '#B08D57', fontFamily: 'Playfair Display,serif', fontWeight: 400 }}>Love</em>
        </p>
      </div>
      <Link to={`/reels/${reelId}`} className="rpd-more-link">View All →</Link>
    </div>
    <div className="rpd-more-grid">
      {otherProducts.map((p) => (
        <MiniCard key={p.id} product={p} reelId={reelId} navigate={navigate} />
      ))}
    </div>
  </div>
)}

        {/* ── Ornament ── */}
        <motion.div
          className="rpd-ornament"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.35 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="rpd-ornament-line" />
          <div className="rpd-diamond" />
          <div className="rpd-ornament-line rpd-ornament-line--right" />
        </motion.div>
      </div>

      <Toast msg={toast.msg} show={toast.show} />
    </>
  );
};

/* ══════════════════════════════════════════════════════════
   STYLES — mirrors ProductDetails.jsx exactly
══════════════════════════════════════════════════════════ */
const pageStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;1,300&display=swap');

  .rpd-page { background: #FAF8F4; min-height: 100vh; padding-top: 162px; font-family: 'Jost', sans-serif; position: relative; overflow-x: hidden; }

  /* Loading */
  .rpd-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 60vh; gap: 20px; position: relative; z-index: 1; }
  .rpd-loading-ring { width: 36px; height: 36px; border: 2px solid #e8e0d5; border-top-color: #B08D57; border-radius: 50%; animation: rpdSpin 0.9s linear infinite; }
  @keyframes rpdSpin { to { transform: rotate(360deg); } }
  .rpd-loading-text { font-family: 'Cormorant Garamond', serif; font-style: italic; font-size: 15px; color: #9A9186; letter-spacing: 0.08em; }

  /* Error */
  .rpd-error-state { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 60vh; gap: 12px; position: relative; z-index: 1; text-align: center; padding: 40px; }
  .rpd-error-icon  { font-size: 28px; color: #B08D57; margin: 0; }
  .rpd-error-title { font-family: 'Playfair Display', serif; font-size: 22px; color: #1C1A18; margin: 0; }
  .rpd-error-back  { margin-top: 8px; font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #B08D57; text-decoration: none; }
  .rpd-error-back:hover { color: #1C1A18; }

  /* Breadcrumb — matches pd-breadcrumb */
  .rpd-breadcrumb { position: relative; z-index: 1; padding: 14px clamp(20px,5vw,64px); display: flex; align-items: center; gap: 8px; font-size: 10px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase; color: #8A7E72; border-bottom: 1px solid rgba(176,141,87,0.12); flex-wrap: wrap; }
  .rpd-breadcrumb a { color: #8A7E72; text-decoration: none; transition: color 0.2s; }
  .rpd-breadcrumb a:hover { color: #B08D57; }
  .rpd-bc-sep { color: rgba(176,141,87,0.5); }
  .rpd-bc-cur { color: #1C1A18; }

  /* Main 2-col grid — matches pd-main */
  .rpd-main { position: relative; z-index: 1; max-width: 1200px; margin: 0 auto; padding: 40px clamp(20px,5vw,64px); display: grid; grid-template-columns: 1fr 1fr; gap: clamp(32px,5vw,72px); align-items: start; }

  /* Gallery — matches pd-gallery */
  .rpd-gallery { position: sticky; top: 180px; }
  .rpd-main-img-wrap { position: relative; margin-bottom: 12px; }
  .rpd-img-badge { position: absolute; top: 12px; left: 12px; font-family: 'Jost', sans-serif; font-size: 8.5px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; padding: 5px 12px; z-index: 3; }
  .rpd-img-off { position: absolute; top: 12px; right: 12px; background: #C0392B; color: #fff; font-size: 8.5px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; padding: 5px 12px; z-index: 3; }
  .rpd-main-img { width: 100%; aspect-ratio: 1/1; overflow: hidden; border-radius: 2px; background: #F0EAE0; margin-bottom: 12px; border: 1px solid rgba(176,141,87,0.12); }
  .rpd-main-img img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.6s ease; }
  .rpd-main-img:hover img { transform: scale(1.04); }
  .rpd-thumbs { display: grid; grid-template-columns: repeat(4,1fr); gap: 8px; }
  .rpd-thumb { aspect-ratio: 1/1; overflow: hidden; border-radius: 2px; border: 1.5px solid transparent; cursor: pointer; background: #F0EAE0; transition: border-color 0.2s; }
  .rpd-thumb.active { border-color: #B08D57; }
  .rpd-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.4s; }
  .rpd-thumb:hover img { transform: scale(1.06); }

  /* Info column */
  .rpd-info { padding-top: 4px; }
  .rpd-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase; color: #B08D57; display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
  .rpd-eyebrow::before { content: ''; width: 20px; height: 1px; background: #B08D57; }
  .rpd-name { font-family: 'Playfair Display', serif; font-size: clamp(22px,3vw,34px); font-weight: 700; color: #1C1A18; line-height: 1.18; letter-spacing: -0.01em; margin-bottom: 14px; }

  /* Stars */
  .rpd-stars { display: flex; align-items: center; gap: 6px; margin-bottom: 16px; }
  .rpd-stars-icons { display: flex; gap: 2px; }
  .rpd-review-count { font-size: 11px; color: #8A7E72; }

  /* Price — matches pd-price-block */
  .rpd-price-block { margin-bottom: 18px; }
  .rpd-price-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 4px; }
  .rpd-price { font-family: 'Jost', sans-serif; font-size: clamp(24px,3vw,32px); font-weight: 600; color: #B08D57; letter-spacing: 0.04em; }
  .rpd-mrp { font-size: 14px; color: #AEA39A; text-decoration: line-through; font-weight: 300; }
  .rpd-discount-badge { font-size: 9px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #FAF8F4; background: #C0392B; padding: 3px 8px; border-radius: 1px; }
  .rpd-save { font-size: 11px; color: #8A7E72; margin-bottom: 4px; letter-spacing: 0.04em; }
  .rpd-tax-note { font-size: 10px; color: #8A7E72; margin-top: 2px; }

  .rpd-divider { width: 100%; height: 1px; background: linear-gradient(to right, rgba(176,141,87,0.25), transparent); margin: 18px 0; }

  .rpd-desc { font-size: 13px; font-weight: 300; color: #5C5247; line-height: 1.85; letter-spacing: 0.02em; margin-bottom: 20px; }

  .rpd-offer-box { background: rgba(176,141,87,0.1); border: 1px solid rgba(176,141,87,0.25); padding: 10px 14px; font-size: 12px; color: #B08D57; margin-bottom: 18px; font-weight: 500; }

  /* Details table */
  .rpd-details { display: flex; flex-direction: column; gap: 9px; margin-bottom: 20px; }
  .rpd-detail-row { display: flex; gap: 12px; font-size: 12px; align-items: center; }
  .rpd-detail-key { font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: #9A9186; width: 90px; flex-shrink: 0; }
  .rpd-detail-val { color: #1C1A18; }

  /* Qty — matches pd-qty-ctrl */
  .rpd-qty-label { font-size: 9px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase; color: #1C1A18; margin-bottom: 8px; }
  .rpd-qty-ctrl { display: flex; align-items: center; border: 1px solid rgba(176,141,87,0.3); width: fit-content; margin-bottom: 20px; }
  .rpd-qty-btn { width: 36px; height: 36px; background: none; border: none; cursor: pointer; color: #5C5247; display: flex; align-items: center; justify-content: center; transition: background 0.2s, color 0.2s; }
  .rpd-qty-btn:hover { background: #B08D57; color: #FAF8F4; }
  .rpd-qty-val { width: 40px; text-align: center; font-family: 'Playfair Display', serif; font-size: 15px; font-weight: 600; color: #1C1A18; border-left: 1px solid rgba(176,141,87,0.2); border-right: 1px solid rgba(176,141,87,0.2); padding: 8px 0; }

  /* CTAs — identical to pd-btn-atc / pd-btn-buy */
  .rpd-actions { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }
  .rpd-btn-atc { width: 100%; background: transparent; color: #1C1A18; border: 1.5px solid #1C1A18; font-family: 'Jost', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase; padding: 15px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; position: relative; overflow: hidden; transition: color 0.28s; }
  .rpd-btn-atc::after { content: ''; position: absolute; inset: 0; background: #1C1A18; transform: translateX(-101%); transition: transform 0.28s cubic-bezier(0.4,0,0.2,1); z-index: 0; }
  .rpd-btn-atc:hover { color: #FAF8F4; }
  .rpd-btn-atc:hover::after { transform: translateX(0); }
  .rpd-btn-atc > * { position: relative; z-index: 1; }
  .rpd-btn-buy { width: 100%; background: #1C1A18; color: #FAF8F4; border: none; font-family: 'Jost', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase; padding: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; position: relative; overflow: hidden; transition: color 0.28s; }
  .rpd-btn-buy::after { content: ''; position: absolute; inset: 0; background: #B08D57; transform: translateX(-101%); transition: transform 0.28s cubic-bezier(0.4,0,0.2,1); z-index: 0; }
  .rpd-btn-buy:hover::after { transform: translateX(0); }
  .rpd-btn-buy > * { position: relative; z-index: 1; }

  /* Wishlist + Share row — matches pd-icon-row */
  .rpd-icon-row { display: flex; gap: 10px; margin-bottom: 22px; }
  .rpd-icon-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 7px; padding: 10px; font-size: 9px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: #5C5247; border: 1px solid rgba(176,141,87,0.25); background: none; cursor: pointer; transition: border-color 0.2s, color 0.2s; }
  .rpd-icon-btn:hover { border-color: #B08D57; color: #B08D57; }
  .rpd-icon-btn--wished { color: #B08D57; border-color: #B08D57; }

  /* Trust grid — matches pd-trust-grid */
  .rpd-trust-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .rpd-trust-card { display: flex; align-items: flex-start; gap: 10px; padding: 12px; background: #F5F0E8; border: 1px solid rgba(176,141,87,0.15); border-radius: 2px; }
  .rpd-trust-icon { color: #B08D57; flex-shrink: 0; margin-top: 1px; }
  .rpd-trust-title { font-size: 10px; font-weight: 600; color: #1C1A18; margin-bottom: 2px; }
  .rpd-trust-sub { font-size: 10px; font-weight: 300; color: #8A7E72; line-height: 1.5; }

  /* More from reel */
  .rpd-more { position: relative; z-index: 1; max-width: 1200px; margin: 0 auto; padding: 0 clamp(20px,5vw,64px) 80px; }
  .rpd-more-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 28px; padding-bottom: 16px; border-bottom: 1px solid rgba(176,141,87,0.15); }
  .rpd-more-label { font-size: 10px; font-weight: 500; letter-spacing: 0.28em; text-transform: uppercase; color: #B08D57; margin-bottom: 4px; }
  .rpd-more-title { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; color: #1C1A18; }
  .rpd-more-link { font-size: 9px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; color: #1C1A18; text-decoration: none; border-bottom: 1px solid #1C1A18; padding-bottom: 2px; transition: color 0.2s, border-color 0.2s; white-space: nowrap; }
  .rpd-more-link:hover { color: #B08D57; border-color: #B08D57; }
  .rpd-more-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; }
  .rpd-mc { background: #fff; cursor: pointer; transition: box-shadow 0.3s, transform 0.25s; border: 1px solid rgba(240,235,228,0.8); }
  .rpd-mc:hover { box-shadow: 0 12px 36px rgba(28,26,24,0.10); transform: translateY(-3px); }
  .rpd-mc-img { aspect-ratio: 1/1; overflow: hidden; background: #F0EAE0; }
  .rpd-mc-img img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.5s; }
  .rpd-mc:hover .rpd-mc-img img { transform: scale(1.05); }
  .rpd-mc-body { padding: 12px 14px 14px; border-top: 1px solid #f5f0e8; }
  .rpd-mc-name { font-family: 'Playfair Display', serif; font-size: 13px; font-weight: 600; color: #1C1A18; margin-bottom: 6px; }
  .rpd-mc-price-row { display: flex; align-items: center; gap: 6px; }
  .rpd-mc-price { font-family: 'Jost', sans-serif; font-size: 14px; font-weight: 600; color: #B08D57; letter-spacing: 0.04em; }
  .rpd-mc-mrp { font-size: 11px; color: #AEA39A; text-decoration: line-through; }

  /* Toast — matches pd-toast */
  .rpd-toast { position: fixed; bottom: 80px; left: 20px; right: 20px; width: auto; background: #1C1A18; color: #FAF8F4; font-family: 'Jost', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 0.1em; padding: 12px 22px; display: flex; align-items: center; justify-content: center; gap: 8px; z-index: 9999; border-radius: 1px; box-shadow: 0 8px 32px rgba(0,0,0,0.22); text-align: center; }
  /* Ornament */
  .rpd-ornament { display: flex; align-items: center; justify-content: center; gap: 16px; margin: 40px 0 60px; position: relative; z-index: 1; }
  .rpd-ornament-line { width: 80px; height: 1px; background: linear-gradient(to right, transparent, #B08D57); }
  .rpd-ornament-line--right { background: linear-gradient(to left, transparent, #B08D57); }
  .rpd-diamond { width: 6px; height: 6px; background: #B08D57; transform: rotate(45deg); flex-shrink: 0; }

  /* Responsive */
  @media (max-width: 860px) {
    .rpd-page    { padding-top: 148px; }
    .rpd-main    { grid-template-columns: 1fr; gap: 28px; }
    .rpd-gallery { position: static; }
    .rpd-more-grid { grid-template-columns: repeat(2,1fr); }
    .rpd-trust-grid { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 480px) {
    .rpd-page { padding-top: 136px; }
    .rpd-trust-grid { grid-template-columns: 1fr; }
    .rpd-more-grid { gap: 10px; }
  }
`;

export default ReelProductDetail;